// Rekapitulacija placa za razdoblje - one line per employee with the totals of
// every obracun whose JOPPD date falls inside the period.
//
// Columns:
//   Ukupno sati   all hour categories of the obracun, the same total the salarie
//                 line itself shows in Ukupno
//   Bruto 2       FinBruto2
//   Neto          FinNeto2 - the net AFTER tax but BEFORE the non taxable additions
//   Neoporezivo   FinAdd1..5_Amount + NaHZZOiznos, the same block EmployeeEdit3 adds
//                 to Neto 2 to get Neto 3
//
// Every obracun is kept under its employee so the report can print either only the
// employee totals or each obracun with a subtotal.

import {round, dynamicSortMultiple} from '@/snippets/allSnippets'
import SalarieService from '@/services/SalarieService'

// Names are sorted the Croatian way. A plain < comparison puts C, C, D, S, Z after
// Z, which scatters the employees over the whole list.
const byName = (a, b) => {
  const name = String(a.FullName).localeCompare(String(b.FullName), 'hr')
  return name !== 0 ? name : String(a.VatID).localeCompare(String(b.VatID))
}

const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

// Every hour column of a salarie line. Ukupno normally holds their sum, but it is
// left at zero on some older lines, so it is only used when it really has a value.
const HOUR_FIELDS = [
  'RedovanRad', 'NocniRad', 'PrekovremeniRad', 'NedjeljaRad', 'BlagdanRad', 'TerenRad',
  'PreraspodjelaRada', 'Pripravnost', 'NeradniBlagdan', 'GodisnjiOdmor',
  'BolovanjeDo42Dana', 'BolovanjeOd42Dana', 'Rodiljni', 'PlaceniDopust',
  'NeplaceniDopust', 'NenazocnostNaZahtjevRadnika', 'ZastojKrivnjomPoslodavca',
  'Strajk', 'Lockout', 'VrijemeMirovanjaRadnogOdnosa', 'Dodatno1', 'Dodatno2'
]

const hoursOf = (det) => {
  const stored = num(det.Ukupno)
  if (stored !== 0) { return round(stored, 2) }
  return round(HOUR_FIELDS.reduce((sum, field) => sum + num(det[field]), 0), 2)
}

// The non taxable part of the payout - identical to FinAddAll in EmployeeEdit3.
const notaxOf = (det) => round(
  num(det.FinAdd1_Amount) + num(det.FinAdd2_Amount) + num(det.FinAdd3_Amount) +
  num(det.FinAdd4_Amount) + num(det.FinAdd5_Amount) + num(det.NaHZZOiznos), 2)

const emptySums = () => ({hours: 0, bruto2: 0, neto: 0, notax: 0, payout: 0})

const addTo = (sums, line) => {
  sums.hours = round(sums.hours + line.hours, 2)
  sums.bruto2 = round(sums.bruto2 + line.bruto2, 2)
  sums.neto = round(sums.neto + line.neto, 2)
  sums.notax = round(sums.notax + line.notax, 2)
  sums.payout = round(sums.payout + line.payout, 2)
}

/**
 * @param {Number} companyid
 * @param {String} fromDate  YYYY-MM-DD
 * @param {String} toDate    YYYY-MM-DD
 * @param {Object} employee  optional, one employee only
 * @returns {Object} {employees: [...], totals: {...}, obracuni: Number}
 */
async function freeSalarieRecap (companyid, fromDate, toDate, employee) {
  const result = {employees: [], totals: emptySums(), obracuni: 0}

  // The report is always for the active company only.
  const CompanyId = parseInt(companyid, 10)
  if (!CompanyId) { return result }

  const query = {
    CompanyId: CompanyId,
    fromDate: fromDate,
    toDate: toDate,
    // 4 - period is read from JoppdDate, which every salarie has
    typeId: 4,
    userId: employee && employee.id ? employee.id : null
  }

  const res = await SalarieService.choosedetail(query)
  const all = res && res.data && res.data.salaries ? res.data.salaries : []
  // the server filters on CompanyId as well, this only makes sure a changed active
  // company can never leave another company's obracuni in the report
  const heads = all.filter(head => head && parseInt(head.CompanyId, 10) === CompanyId)

  // VatID is filled in on every salarie line, so it is what holds the obracuni of
  // one person together even when the employee was re-entered under a new id.
  const byPerson = {}

  heads.forEach(head => {
    if (!head || !head.SalarieDetails) { return }
    const vrsta = head.FreeF7 ? head.FreeF7 : ''

    head.SalarieDetails.forEach(det => {
      const key = det.VatID ? String(det.VatID).trim() : ('emp' + det.empID)
      if (!byPerson[key]) {
        // over a thousand salarie lines carry a trailing space in First or Last,
        // which shows up in the printed name and throws the sorting off
        const first = det.First ? String(det.First).trim() : ''
        const last = det.Last ? String(det.Last).trim() : ''
        byPerson[key] = {
          VatID: det.VatID ? String(det.VatID).trim() : '',
          empID: det.empID ? det.empID : null,
          First: first,
          Last: last,
          FullName: (last ? last + ' ' : '') + first,
          eemail: det.eemail ? det.eemail : '',
          lines: [],
          totals: emptySums()
        }
      }

      const neto = round(num(det.FinNeto2), 2)
      const notax = notaxOf(det)
      const line = {
        Month: det.Month ? det.Month : null,
        Year: det.Year ? det.Year : null,
        Order: head.Order ? head.Order : null,
        salarieID: det.salarieID ? det.salarieID : null,
        BrojJoppd: head.BrojJoppd ? head.BrojJoppd : '',
        JoppdDate: head.JoppdDate ? head.JoppdDate : null,
        VrstaObracuna: vrsta,
        hours: hoursOf(det),
        bruto2: round(num(det.FinBruto2), 2),
        neto: neto,
        notax: notax,
        payout: round(neto + notax, 2)
      }

      byPerson[key].lines.push(line)
      addTo(byPerson[key].totals, line)
      addTo(result.totals, line)
      result.obracuni += 1
    })
  })

  result.employees = Object.keys(byPerson).map(key => byPerson[key])
  result.employees.sort(byName)
  result.employees.forEach(person => {
    person.lines.sort(dynamicSortMultiple('Year', 'Month', 'JoppdDate'))
  })

  return result
}

export {freeSalarieRecap}
